import { HDD_DEVICE_TYPE } from './surveyQa';

/**
 * Reads the backend-computed minimum-curvature positions off a survey's
 * approved shots, ordered for display.
 *
 * The web app no longer computes minimum curvature itself — the server
 * populates each shot's PositionSet on GET (see Services/SurveyPositions.cs).
 * This just filters to approved shots, orders them the way each tool should be
 * traced, and exposes the fields the charts and tables consume.
 *
 * Ordering mirrors Services/SurveyPositions.cs: continuous FastTrak surveys are
 * a chronological round-trip, so they are ordered by timestamp; HDD keeps
 * capture order (the Joint sequence, as stored); every other tool is ordered by
 * depth ascending. Positions are taken verbatim from PositionSet, so HDD
 * surveys carry their absolute (tie-in) coordinates.
 *
 * @returns {Array<{shot: object, id: string, depth: number, northing: number, easting: number, tvd: number, stepout: number}>}
 */
export function orderedPositions(survey) {
    const shots = (survey.SavedShots || []).filter(s => s.Approved !== false);
    if (shots.length === 0) return [];
    const isContinuous = (survey.DeviceName || '').startsWith('FastTrak');
    const isHdd = survey.DeviceType === HDD_DEVICE_TYPE;
    let ordered;
    if (isContinuous) {
        ordered = [...shots].sort((a, b) => new Date(a.Timestamp) - new Date(b.Timestamp));
    } else if (isHdd) {
        ordered = shots;
    } else {
        ordered = [...shots].sort((a, b) => a.Depth - b.Depth);
    }
    return ordered.map(s => {
        const ps = s.PositionSet || {};
        return {
            shot: s,
            id: s.Id,
            depth: s.Depth,
            northing: ps.Northing ?? 0,
            easting: ps.Easting ?? 0,
            tvd: ps.Elevation ?? 0,
            stepout: ps.Stepout ?? 0
        };
    });
}

/**
 * The same positions keyed by shot, for tables that render rows in their own
 * order and need each row's position back.
 *
 * Keyed by the shot object itself rather than by `Id`: HDD shots have no Id at
 * all (the tool never assigns one), so an Id-keyed map collapsed to a single
 * entry and every HDD row showed the last shot's position. Callers filter and
 * sort `SavedShots`, which preserves the object identities this map uses.
 */
export function positionsByShot(survey) {
    const map = new Map();
    orderedPositions(survey).forEach(p => map.set(p.shot, p));
    return map;
}
